import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useCallback, useEffect, useRef, useState } from "react";
import { Cart } from "store/apis/products";
import {
  StateRegistrationName,
  StateRegistrationNameType,
  StateRegistrationsRes,
  useSubmitRegistrationIntakeMutation,
} from "store/apis/StateRegistrations";
import { useGetEntityDataAttributesQuery } from "store/apis/entity";
import { RegistrationStatusName } from "store/apis/registrations";
import { BillingInvoice } from "types/Models/billing";
import { BackendError } from "types/utils/error";
import {
  DONE_STATUSES,
  REGISTRATION_TYPES,
  ROW_WHAT_INKLE_DOES,
} from "./constants";
import { BannerStep1, RowStep1 } from "./StepViews";
import { OnboardingEntryPoint, RowEntryConfig } from "./types";
import { ForeignQualificationTierModal } from "./ForeignQualificationTierModal";
import { ForeignQualificationPricingTier } from "constants/foreignQualificationPricingTier";

export type { OnboardingEntryPoint };

type IntakeEntry = {
  type: StateRegistrationNameType;
  fields: Record<string, string>;
};

// ─── Types ────────────────────────────────────────────────────────────────────

export type RegistrationOnboardingProps = {
  open: boolean;
  onClose: () => void;
  stateData: StateRegistrationsRes;
  initialServices?: StateRegistrationNameType[];
  // Skips the service-selection screen and proceeds straight to the pricing
  // tier / submit flow — for callers (e.g. the "Add state" wizard) that
  // already collected the service selection themselves.
  skipServiceSelection?: boolean;
  onIntakeComplete?: (cart: Cart, invoice: BillingInvoice | null) => void;
} & (
  | { entryPoint: "banner"; rowConfig?: never }
  | { entryPoint: "row"; rowConfig: RowEntryConfig }
);

// ─── Main component ───────────────────────────────────────────────────────────

export const RegistrationOnboarding = ({
  open,
  onClose,
  entryPoint,
  stateData,
  rowConfig,
  initialServices,
  skipServiceSelection = false,
  onIntakeComplete,
}: RegistrationOnboardingProps) => {
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const query = useQuery();
  const entityId = query.get("entity") ?? "";
  const { isForeignCA } = useRoleBasedView();

  const [submitIntake, { isLoading: isSaving }] =
    useSubmitRegistrationIntakeMutation();

  const checkoutModal = useModal();
  const openCheckoutModal = checkoutModal.open;
  const [checkoutCartData, setCheckoutCartData] = useState<Cart | null>(null);
  const [checkoutInvoice, setCheckoutInvoice] = useState<BillingInvoice | null>(
    null
  );
  const [showServiceModal, setShowServiceModal] = useState(true);

  const tierModal = useModal();
  const openTierModal = tierModal.open;
  const [pendingIntakeEntries, setPendingIntakeEntries] = useState<
    IntakeEntry[]
  >([]);

  const stateName = stateData.state;
  const stateId = stateData.state_id;

  const { data: entityAttributes = [] } = useGetEntityDataAttributesQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );

  const entityName =
    (entityAttributes.find((a) => a.name === "entity_name")?.value as
      | string
      | undefined) ?? "";

  const pendingTypes = REGISTRATION_TYPES.filter((type) => {
    if (
      stateData.is_home_state &&
      type === StateRegistrationName.SECRETARY_OF_STATE
    ) {
      return false;
    }
    const reg = stateData[type];
    return (
      !DONE_STATUSES.includes(reg.status) &&
      reg.status !== RegistrationStatusName.NOT_APPLICABLE
    );
  });

  const [selectedServices, setSelectedServices] = useState<
    StateRegistrationNameType[]
  >(initialServices ?? pendingTypes);

  const isRADone =
    DONE_STATUSES.includes(
      stateData[StateRegistrationName.REGISTERED_AGENT].status
    ) ||
    stateData[StateRegistrationName.REGISTERED_AGENT].status ===
      RegistrationStatusName.NOT_APPLICABLE ||
    stateData[StateRegistrationName.REGISTERED_AGENT].cart_status === "IN_CART";

  const toggleService = (type: StateRegistrationNameType) => {
    setSelectedServices((prev) => {
      const isRemoving = prev.includes(type);

      if (isRemoving) {
        const next = prev.filter((t) => t !== type);
        if (type === StateRegistrationName.SECRETARY_OF_STATE) {
          return next.filter(
            (t) => t !== StateRegistrationName.REGISTERED_AGENT
          );
        }
        return next;
      }

      const next = [...prev, type];
      if (
        type === StateRegistrationName.SECRETARY_OF_STATE &&
        !isRADone &&
        !next.includes(StateRegistrationName.REGISTERED_AGENT)
      ) {
        return [...next, StateRegistrationName.REGISTERED_AGENT];
      }
      return next;
    });
  };

  const handleClose = () => {
    setSelectedServices(pendingTypes);
    setShowServiceModal(true);
    onClose();
  };

  const submitEntries = useCallback(
    async (intakeEntries: IntakeEntry[]) => {
      try {
        const { invoice, cart_details: cart } = await submitIntake({
          groupId,
          entityId,
          stateId,
          intakeEntries,
        }).unwrap();
        if (onIntakeComplete) {
          onIntakeComplete(cart, invoice);
          return;
        }

        setShowServiceModal(false);
        setCheckoutCartData(cart);
        setCheckoutInvoice(invoice);
        openCheckoutModal();
      } catch (error) {
        alertToast({ message: (error as BackendError)?.data?.error?.message });
      }
    },
    [
      alertToast,
      openCheckoutModal,
      entityId,
      groupId,
      onIntakeComplete,
      stateId,
      submitIntake,
    ]
  );

  const handleConfirmClick = useCallback(async () => {
    if (!groupId || !entityId) return;

    let typesToSubmit =
      entryPoint === "banner" ? selectedServices : [rowConfig!.type];

    if (
      entryPoint === "row" &&
      rowConfig!.type === StateRegistrationName.SECRETARY_OF_STATE &&
      !isRADone &&
      !typesToSubmit.includes(StateRegistrationName.REGISTERED_AGENT)
    ) {
      typesToSubmit = [
        ...typesToSubmit,
        StateRegistrationName.REGISTERED_AGENT,
      ];
    }

    const intakeEntries = typesToSubmit.map((type) => ({
      type,
      fields: {},
    }));

    if (typesToSubmit.includes(StateRegistrationName.SECRETARY_OF_STATE)) {
      setPendingIntakeEntries(intakeEntries);
      openTierModal();
      return;
    }

    await submitEntries(intakeEntries);
  }, [
    entityId,
    entryPoint,
    groupId,
    isRADone,
    rowConfig,
    selectedServices,
    submitEntries,
    openTierModal,
  ]);

  const hasAutoConfirmedRef = useRef(false);

  useEffect(() => {
    if (
      open &&
      skipServiceSelection &&
      groupId &&
      entityId &&
      !hasAutoConfirmedRef.current
    ) {
      hasAutoConfirmedRef.current = true;
      void handleConfirmClick();
    }
  }, [open, skipServiceSelection, groupId, entityId, handleConfirmClick]);

  const handleTierConfirm = async (tier: ForeignQualificationPricingTier) => {
    const entriesWithTier = pendingIntakeEntries.map((entry) =>
      entry.type === StateRegistrationName.SECRETARY_OF_STATE
        ? { ...entry, fields: { ...entry.fields, pricing_tier: tier } }
        : entry
    );
    await submitEntries(entriesWithTier);
    tierModal.close();
  };

  const closeCheckoutModal = () => {
    checkoutModal.close();
    setCheckoutCartData(null);
    setCheckoutInvoice(null);
    handleClose();
  };

  const onCartPaid = () => {
    closeCheckoutModal();
    successToast({
      message: `${stateName} registration payment successful!`,
    });
  };

  const canConfirm =
    entryPoint === "banner"
      ? selectedServices.length > 0
      : pendingTypes.length > 0;

  const modalTitle =
    entryPoint === "row"
      ? ROW_WHAT_INKLE_DOES[rowConfig!.type].eyebrow
      : entityName || stateName;

  return (
    <>
      <Modal.Root
        open={open && showServiceModal && !skipServiceSelection}
        onOpenChange={(isOpen) => !isOpen && handleClose()}
      >
        <Modal.Content useCustomOverlay>
          <Modal.Header className="sm:t-pl-4">
            <Modal.Title>{modalTitle}</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-flex t-flex-col t-gap-6 sm:t-px-4">
            {entryPoint === "banner" && (
              <span className="t-text-body-sm t-text-text-30">
                Setting up compliance for {stateName}
              </span>
            )}
            {entryPoint === "banner" && (
              <div className="t-text-body t-text-text-60">
                Select what you'd like Inkle to handle. We take care of the
                filing and all state correspondence on your behalf.
              </div>
            )}
            {entryPoint === "banner" ? (
              <BannerStep1
                stateData={stateData}
                selected={selectedServices}
                onToggle={toggleService}
                stateName={stateName}
              />
            ) : (
              <RowStep1 type={rowConfig!.type} />
            )}
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button type="button" customType="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="button"
              customType="primary"
              isLoading={isSaving}
              disabled={!canConfirm || isSaving}
              onClick={() => void handleConfirmClick()}
            >
              Confirm
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
      {checkoutModal.isOpen && checkoutCartData && (
        <CheckoutModal
          type="cart"
          cart={checkoutCartData}
          cartInvoice={checkoutInvoice}
          onCartPaid={onCartPaid}
          title="Cart Payment"
          open={checkoutModal.isOpen}
          onClose={closeCheckoutModal}
          payerUserType={isForeignCA && "FCA"}
        />
      )}
      {tierModal.isOpen && (
        <ForeignQualificationTierModal
          open={tierModal.isOpen}
          groupId={groupId ?? ""}
          entityId={entityId}
          stateId={stateId}
          stateName={stateName}
          isConfirming={isSaving}
          onConfirm={(tier) => void handleTierConfirm(tier)}
          onClose={() => tierModal.close()}
        />
      )}
    </>
  );
};
